import { ref } from 'vue'

/**
 * Base64url encoding/decoding helpers for WebAuthn ArrayBuffer <-> string transport.
 */
function bufferToBase64url(buffer) {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i])
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function base64urlToBuffer(base64url) {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes.buffer
}

function getErrorMessage(err) {
  if (err?.name === 'NotAllowedError') {
    return 'Passkey request was cancelled or not allowed.'
  }
  if (err?.name === 'SecurityError') {
    return 'Passkey operation is not allowed in this context.'
  }
  if (err?.name === 'AbortError') {
    return 'Passkey request timed out.'
  }
  if (err?.name === 'InvalidStateError') {
    return 'A passkey already exists for this account on this device.'
  }
  return err?.message || 'Passkey operation failed. Please try again.'
}

/**
 * Prepare server-provided authentication options for the browser credential API.
 * Decodes base64url-encoded challenge and allowCredentials IDs.
 */
function prepareAuthOptions(serverOptions) {
  const { challenge, rpId, allowCredentials, timeout, userVerification } = serverOptions
  const publicKey = {
    challenge: base64urlToBuffer(challenge),
    ...(rpId && { rpId }),
    ...(timeout && { timeout }),
    ...(userVerification && { userVerification }),
  }
  if (allowCredentials) {
    publicKey.allowCredentials = allowCredentials.map((cred) => ({
      ...cred,
      id: base64urlToBuffer(cred.id),
    }))
  }
  return { publicKey }
}

/**
 * Prepare server-provided registration options for the browser credential API.
 * Decodes base64url-encoded challenge and user.id.
 */
function prepareRegistrationOptions(serverOptions) {
  const { challenge, rp, user, pubKeyCredParams, timeout, attestation, authenticatorSelection, excludeCredentials, extensions } = serverOptions
  const publicKey = {
    challenge: base64urlToBuffer(challenge),
    ...(rp && { rp }),
    ...(user && {
      user: {
        ...user,
        id: base64urlToBuffer(user.id),
      },
    }),
    ...(pubKeyCredParams && { pubKeyCredParams }),
    ...(timeout && { timeout }),
    ...(attestation && { attestation }),
    ...(authenticatorSelection && { authenticatorSelection }),
    ...(extensions && { extensions }),
  }
  if (excludeCredentials) {
    publicKey.excludeCredentials = excludeCredentials.map((cred) => ({
      ...cred,
      id: base64urlToBuffer(cred.id),
    }))
  }
  return { publicKey }
}

export function usePasskeyAuth() {
  const isSupported = ref(
    typeof window !== 'undefined' && !!window.PublicKeyCredential,
  )
  const isLoading = ref(false)
  const error = ref(null)

  async function authenticate(serverOptions) {
    error.value = null
    isLoading.value = true
    try {
      const credentialRequestOptions = prepareAuthOptions(serverOptions)
      const assertion = await navigator.credentials.get(credentialRequestOptions)
      if (!assertion) {
        throw new DOMException('No credential was selected.', 'NotAllowedError')
      }
      return {
        id: assertion.id,
        rawId: bufferToBase64url(assertion.rawId),
        type: assertion.type,
        response: {
          authenticatorData: bufferToBase64url(assertion.response.authenticatorData),
          clientDataJSON: bufferToBase64url(assertion.response.clientDataJSON),
          signature: bufferToBase64url(assertion.response.signature),
          userHandle: assertion.response.userHandle
            ? bufferToBase64url(assertion.response.userHandle)
            : null,
        },
      }
    } catch (err) {
      error.value = getErrorMessage(err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  async function register(serverOptions) {
    error.value = null
    isLoading.value = true
    try {
      const credentialCreationOptions = prepareRegistrationOptions(serverOptions)
      const credential = await navigator.credentials.create(credentialCreationOptions)
      if (!credential) {
        throw new DOMException('No credential was created.', 'NotAllowedError')
      }
      return {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
          attestationObject: bufferToBase64url(credential.response.attestationObject),
          clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
        },
      }
    } catch (err) {
      error.value = getErrorMessage(err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  // Note: does not set error/isLoading refs — conditional mediation runs silently in the background
  async function authenticateConditional(serverOptions, abortSignal) {
    const credentialRequestOptions = prepareAuthOptions(serverOptions)
    delete credentialRequestOptions.publicKey.timeout
    credentialRequestOptions.mediation = 'conditional'
    if (abortSignal) {
      credentialRequestOptions.signal = abortSignal
    }
    const assertion = await navigator.credentials.get(credentialRequestOptions)
    if (!assertion) return null
    return {
      id: assertion.id,
      rawId: bufferToBase64url(assertion.rawId),
      type: assertion.type,
      response: {
        authenticatorData: bufferToBase64url(assertion.response.authenticatorData),
        clientDataJSON: bufferToBase64url(assertion.response.clientDataJSON),
        signature: bufferToBase64url(assertion.response.signature),
        userHandle: assertion.response.userHandle
          ? bufferToBase64url(assertion.response.userHandle)
          : null,
      },
    }
  }

  async function isConditionalMediationAvailable() {
    try {
      return (
        typeof window !== 'undefined' &&
        !!window.PublicKeyCredential &&
        typeof window.PublicKeyCredential.isConditionalMediationAvailable === 'function' &&
        (await window.PublicKeyCredential.isConditionalMediationAvailable())
      )
    } catch {
      return false
    }
  }

  return { isSupported, isLoading, error, authenticate, authenticateConditional, isConditionalMediationAvailable, register }
}
